/**
 * API Utility Functions
 * ---------------------
 * Utility functions for API-related operations
 */

/**
 * Get the API base URL from env only (local, testing, prod).
 * Used for Tomcat/main APIs (menu, home, user-context, etc.) – typically port 8080.
 * Expects a single URL in VITE_API_BASE_URL; no comma-separated or default logic.
 */
export const getApiBaseUrl = (): string => {
  const raw =
    typeof window === 'undefined'
      ? process.env.VITE_API_BASE_URL ?? (import.meta as unknown as { env?: Record<string, unknown> }).env?.VITE_API_BASE_URL
      : (import.meta as unknown as { env?: Record<string, unknown> }).env?.VITE_API_BASE_URL;

  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  // Relative API bases ("/", "/api") should resolve to an absolute URL on the
  // client for runtime consistency with SSR (both sides use absolute targets).
  if (trimmed.startsWith('/')) {
    if (typeof window !== 'undefined') {
      try {
        const absolute = new URL(trimmed, window.location.origin);
        const normalizedPath = absolute.pathname.replace(/\/+$/, '');
        return `${absolute.origin}${normalizedPath}`;
      } catch {
        return window.location.origin.replace(/\/+$/, '');
      }
    }
    // On the server we keep relative values as-is; SSR request scope can
    // provide an absolute URL when needed.
    return trimmed === '/' ? '/' : `/${trimmed.replace(/^\/+|\/+$/g, '')}`;
  }

  try {
    new URL(trimmed);
    return trimmed.replace(/\/+$/, '');
  } catch {
    return '';
  }
};

/**
 * Base URL for Auth API.
 * For now this uses the same origin as other Tomcat APIs via getApiBaseUrl().
 * If auth moves to a separate host later, this helper can be updated in one place.
 */
export const getAuthApiBaseUrl = (): string => getApiBaseUrl();

/**
 * Base URL for the page service API (separate from Tomcat; backend service name: "page").
 * - Tomcat (menu, home, user-context, etc.) → VITE_API_BASE_URL (e.g. port 8080).
 * - Page service (/page/browse, etc.) → VITE_PAGE_API_BASE_URL (no hardcoded host/port).
 *
 * Set VITE_PAGE_API_BASE_URL in each environment (e.g. local: http://localhost:7090,
 * production: your page service URL). When unset, falls back to getApiBaseUrl() so
 * a single backend can serve both if needed. Server reads env via loadServerEnv().
 */
export const getPageApiBaseUrl = (): string => {
  const env =
    typeof window === 'undefined'
      ? process.env
      : (import.meta as unknown as { env?: Record<string, unknown> }).env as Record<string, unknown> | undefined;

  const raw = env?.VITE_PAGE_API_BASE_URL;
  if (typeof raw === 'string' && raw.trim()) {
    const trimmed = raw.trim();
    try {
      new URL(trimmed);
      return trimmed.replace(/\/+$/, '');
    } catch {
      // fall through
    }
  }

  return getApiBaseUrl();
};

/**
 * Get the website base URL (origin/domain) for building web routes.
 * Prefers `window.GLOBAL_CTX` when provided by the host page.
 */
export const getWebBaseUrl = (): string => {
  if (typeof window === 'undefined') return '';

  // Optional override for local/testing environments.
  // If not set, we fall back to GLOBAL_CTX / current origin.
  const envOverride = import.meta.env.VITE_WEB_BASE_URL || import.meta.env.VITE_APP_WEB_URL;
  if (envOverride && envOverride.trim()) {
    return envOverride.trim().replace(/\/+$/, '');
  }

  const raw = (window as unknown as { GLOBAL_CTX?: unknown }).GLOBAL_CTX;
  if (typeof raw === 'string' && raw.trim()) {
    return raw.replace(/\/+$/, '');
  }

  return window.location.origin.replace(/\/+$/, '');
};

/**
 * Base URL for static assets served from CDN (or fallback to Vite base).
 * Use for any asset that should load from the CDN (e.g. footer app store icons).
 * Returns a string that always ends with `/` so you can append filenames.
 */
export const getCdnBaseUrl = (): string => {
  const base =
    import.meta.env.VITE_CDN_SERVE_URL || import.meta.env.BASE_URL || '';
  return base.replace(/\/?$/, '/');
};

/**
 * Build absolute or root-relative href from a landing URL (e.g. from APIs).
 * Uses getApiBaseUrl() when set so links use the configured base; otherwise root-relative path.
 */
export function buildLandingHref(landingUrl: string | null): string | undefined {
  if (!landingUrl?.trim()) return undefined;
  const trimmed = landingUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const base = getApiBaseUrl();
  return base ? `${base.replace(/\/+$/, '')}${path}` : path;
}

/**
 * Feature flag for image search UI + API call.
 * Defaults to enabled when the env var is not set.
 */
export const isImageSearchEnabled = (): boolean => {
  const raw = import.meta.env.VITE_ENABLE_IMAGE_SEARCH;
  if (!raw) return true;

  const v = raw.trim().toLowerCase();
  return !['0', 'false', 'off', 'no', 'disabled'].includes(v);
};

